import React, { useEffect, useRef } from 'react'

export interface DefineAdSlotProps {
  adUnitPath: string
  size: googletag.GeneralSize
  slotId: string
  targeting?: Record<string, string | string[]>
}

const DefineAdSlot = ({
  adUnitPath,
  size,
  slotId,
  targeting
}: DefineAdSlotProps) => {
  const adRef = useRef<HTMLDivElement>(null)
  const slotRef = useRef<googletag.Slot | null>(null)

  useEffect(() => {
    if (!window.googletag || !adRef.current) {
      console.warn(`DefineAdSlot: GPT not ready or adRef not available for slotId: ${slotId}`)
      return
    }

    if (adRef.current.id !== slotId) {
      adRef.current.id = slotId
    }

    window.googletag.cmd.push(() => {
      const existingSlots = window.googletag!.pubads().getSlots()
      const existingSlot = existingSlots.find(slot => slot.getSlotElementId() === slotId)

      if (existingSlot) {
        slotRef.current = existingSlot
        console.log(`DefineAdSlot: Reusing existing slot ${slotId}`)
        return
      }

      const newSlot = window.googletag!.defineSlot(adUnitPath, size, slotId)

      if (newSlot) {
        slotRef.current = newSlot
        newSlot.addService(window.googletag!.pubads())

        if (targeting) {
          newSlot.setConfig({ targeting })
        }

        window.googletag!.display(slotId)
        console.log(`DefineAdSlot: Ad slot ${slotId} defined and display called.`)
      } else {
        console.warn(`DefineAdSlot: Failed to define ad slot: ${slotId} at path ${adUnitPath}`)
      }
    })

    // Don't destroy slots on unmount when using enableSingleRequest
    // This prevents ads from reloading when components re-render
  }, [adUnitPath, size, slotId, targeting])

  return (
    <div
      ref={adRef}
      style={{ width: '100%', minHeight: '1px' }}
    />
  )
}

export default DefineAdSlot
